import React, { useState } from "react";
import { toast } from "sonner";
import { RotateCw } from "lucide-react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";

/**
 * Reusable "Solicitar reabertura / reavaliação" button + dialog for schools.
 *
 * Props:
 *  - budget: {id, number, status, reopen_requested}
 *  - onDone?: () => void  (parent refresh)
 *  - variant?: "card" (inline card button, full width) | "detail" (small, header)
 *  - testid?: string
 */
export default function RequestReopenButton({ budget, onDone, variant = "card", testid }) {
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState("");
  const [sending, setSending] = useState(false);

  const already = !!budget?.reopen_requested;
  const label = already ? "Solicitação enviada" : "Solicitar reavaliação";

  const send = async () => {
    setSending(true);
    try {
      const r = await api.post(`/school/budgets/${budget.id}/request-reopen`, { reason });
      if (r.data?.already) {
        toast.info("Já existe uma solicitação pendente para este orçamento.");
      } else {
        toast.success("Solicitação enviada! A AMC vai revisar.");
      }
      setOpen(false);
      setReason("");
      onDone?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao solicitar reavaliação");
    } finally {
      setSending(false);
    }
  };

  const btnClass =
    variant === "detail"
      ? "h-9 text-xs"
      : "w-full h-10 text-sm";

  return (
    <>
      <Button
        type="button"
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          if (!already) setOpen(true);
        }}
        disabled={already || sending}
        data-testid={testid || `btn-request-reopen-${budget?.number}`}
        className={`${btnClass} ${
          already
            ? "bg-slate-200 text-slate-500 cursor-default"
            : "bg-orange-600 hover:bg-orange-700 text-white"
        }`}
      >
        <RotateCw className="w-4 h-4 mr-1.5" />
        {label}
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="request-reopen-dialog">
          <DialogHeader>
            <DialogTitle>Solicitar reavaliação</DialogTitle>
            <DialogDescription>
              Orçamento Nº <strong>{budget?.number}</strong> — descreva brevemente o motivo (opcional). A AMC vai revisar e, se possível, reenviar o orçamento atualizado.
            </DialogDescription>
          </DialogHeader>
          <div className="py-2">
            <Textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Ex: a diretora pediu para revisar o item 3, ou o prazo mudou, etc."
              rows={4}
              data-testid="input-reopen-reason"
              className="resize-none"
            />
          </div>
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button
              variant="outline"
              onClick={() => setOpen(false)}
              disabled={sending}
              data-testid="btn-cancel-reopen"
              className="w-full sm:w-auto"
            >
              Cancelar
            </Button>
            <Button
              onClick={send}
              disabled={sending}
              data-testid="btn-send-reopen"
              className="w-full sm:w-auto bg-orange-600 hover:bg-orange-700 text-white"
            >
              {sending ? "Enviando..." : "Enviar solicitação"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

/**
 * Statuses where reopening/reavaliating a budget is NOT allowed.
 * (approved and after — the budget is already committed or finished)
 */
export const FINAL_STATUSES = new Set([
  "aprovado",
  "iniciando",
  "finalizado",
  "nota_fiscal_gerada",
  "nota_faturada",
]);

export function canRequestReopen(status) {
  if (!status) return false;
  return !FINAL_STATUSES.has(status);
}
